import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { notification, Spin, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { cloneDeep, sortBy } from 'lodash';

import Modal from '../../components/uiComponent/modal/index';
import { SubmitListingsFeed, GetBuyBoxPrices } from '../../slices/3-layer-products';
import { refreshBuyBoxPrice } from '../../slices/catalog';
import AmazonLogo from '../../assets/images/newImages/amazon-short-logo.svg';
import ProductImg from '../../assets/images/newImages/nestle.svg';
import copyIcona from '../../assets/images/newImages/copyIcona.svg';
import { copyToClip } from '../../utils/utils';

/* ─── Theme colors (aligned with app theme-variables) ───────────────────── */
const C = {
  primary:    '#1565D8',
  primary50:  '#EEF4FD',
  primary100: '#DCE9FB',
  text:       '#1F1F1F',
  text2:      '#535E63',
  text3:      '#7E8A90',
  border:     '#E8ECF1',
  borderSoft: '#F0F2F5',
  bg:         '#F4F6F6',
  bgLight:    '#FAFBFC',
  danger:     '#F52C50',
  dangerBg:   '#FDECEF',
  success:    '#009c34',
  successBg:  '#E8F7EF',
  warning:    '#B45309',
  warningBg:  '#FEF3E2',
  dark:       '#0F172A'
};

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const Ic = ({ size = 16, stroke = 1.75, children, style, color, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color || 'currentColor'} strokeWidth={stroke}
    strokeLinecap="round" strokeLinejoin="round" style={style} {...rest}>
    {children}
  </svg>
);
const I = {
  X:       p => <Ic {...p}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></Ic>,
  Check:   p => <Ic {...p}><polyline points="20 6 9 17 4 12"/></Ic>,
  Minus:   p => <Ic {...p}><line x1="5" y1="12" x2="19" y2="12"/></Ic>,
  Trash:   p => <Ic {...p}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></Ic>,
  Truck:   p => <Ic {...p}><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></Ic>,
  Wallet:  p => <Ic {...p}><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></Ic>,
  Search:  p => <Ic {...p}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></Ic>,
  ChevD:   p => <Ic {...p}><path d="m6 9 6 6 6-6"/></Ic>,
  Warn:    p => <Ic {...p}><path d="m10.29 3.86-8.13 14.1a1 1 0 0 0 .86 1.5h16.26a1 1 0 0 0 .86-1.5L12 4a1 1 0 0 0-1.71-.14z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></Ic>,
  Pencil:  p => <Ic {...p}><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></Ic>,
  AmzA:    p => <Ic {...p} stroke="none" fill="currentColor"><path d="M12 2 2 22h4l1.5-3.5h9L18 22h4zm-3 12.5L12 8l3 6.5z"/></Ic>
};

/* ─── SegPill: FBA / FBM toggle ──────────────────────────────────────────── */
const SegPill = ({ value, onChange }) => (
  <div style={{ display:'inline-flex', background:'#F2F3F5', borderRadius:8, padding:2 }}>
    {['FBA','FBM'].map(o => (
      <button key={o} onClick={() => onChange(o)} style={{
        padding:'3px 14px', borderRadius:6, border:'none', fontSize:11, fontWeight:600,
        background: value === o ? '#fff' : 'transparent',
        color:      value === o ? C.text2 : C.text3,
        cursor:'pointer', transition:'all .12s',
        boxShadow: value === o ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
        fontFamily:'inherit'
      }}>{o}</button>
    ))}
  </div>
);

/* ─── Inline checkbox ────────────────────────────────────────────────────── */
const Chk = ({ checked, indeterminate, onChange, size = 18, disabled }) => {
  const ref = React.useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = !!indeterminate; }, [indeterminate]);
  return (
    <input
      type="checkbox"
      ref={ref}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      style={{
        width: size, height: size, flexShrink:0,
        margin: 0, cursor: disabled ? 'not-allowed' : 'pointer',
        accentColor: C.primary,
        opacity: disabled ? 0.5 : 1
      }}
    />
  );
};

/* ─── Price input ────────────────────────────────────────────────────────── */
const PriceInput = ({ value, onChange, error, cogs }) => {
  const [focused, setFocused] = useState(false);
  const sugg = cogs && Number(cogs) > 0 ? (Number(cogs) * 2.5).toFixed(2) : null;
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
      <div style={{
        display:'flex', alignItems:'center', height:36,
        border: `1px solid ${error && !focused ? C.danger : focused ? C.primary : C.border}`,
        borderRadius:6, overflow:'hidden', background:'#fff',
        boxShadow: focused ? `0 0 0 3px ${C.primary100}` : error && !focused ? '0 0 0 2px #FDECEF' : 'none',
        transition:'all .15s'
      }}>
        <span style={{ padding:'0 14px', color:C.text3, fontSize:12, borderRight:`1px solid ${C.borderSoft}`, lineHeight:'34px' }}>$</span>
        <input
          type="number" min={0} step={0.01} value={value}
          onChange={e => onChange(e.target.value)}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          placeholder="0.00"
          style={{ flex:1, minWidth:0, border:'none', outline:'none', padding:'0 16px', fontSize:13, color:C.text, background:'transparent', fontFamily:'inherit' }}
        />
      </div>
    </div>
  );
};

/* ─── Horizontal bulk panel (absolute-positioned below bar) ──────────────── */
const BulkPanel = ({ kind, count, selectedRows, onApply, onClose }) => {
  const [val, setVal]       = useState('');
  const [mode, setMode]     = useState('fixed');
  const [bbSign, setBbSign] = useState('-');
  const [bbOffset, setBbOffset] = useState('');
  
  const wrap = {
    position:'absolute', left:16, right:16, top:'calc(100% - 1px)',
    background:'#fff', border:`1px solid ${C.border}`, borderRadius:14,
    boxShadow:'0 25px 50px -12px rgba(15,23,42,0.25), 0 0 0 1px rgba(0,0,0,0.02)', zIndex:20, padding:'14px 14px',
    animation:'blm-slideUp .25s cubic-bezier(0.16, 1, 0.3, 1) forwards', color:C.text,
    fontFamily:'inherit'
  };

  const closeBtn = (
    <button onClick={onClose} style={{ width:26, height:26, borderRadius:5, border:'none', background:'transparent', cursor:'pointer', color:C.text3, display:'grid', placeItems:'center', flexShrink:0 }}>
      <I.X size={13}/>
    </button>
  );

  if (kind === 'fulfill') return (
    <div style={wrap}>
      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
        <div style={{ flexShrink:0 }}>
          <div style={{ fontSize:13, fontWeight:700 }}>Set fulfillment</div>
          <div style={{ fontSize:12, color:C.text3, marginTop:2 }}>{count} SKUs · choose one</div>
        </div>
        <div style={{ display:'flex', gap:10, flex:1 }}>
          {[
            { v:'FBA', t:'Fulfilled by Amazon',  bg:'#FFF7E0', color:'#B45309', icon: <I.AmzA size={16}/> },
            { v:'FBM', t:'Fulfilled by Merchant', bg:C.primary50, color:C.primary, icon: <I.Truck size={16}/> }
          ].map(o => (
            <button key={o.v} onClick={() => { 
              onApply(rows => rows.map(r => {
                if (!selectedRows.find(s => s.id === r.id)) return r;
                const base = r.baseSku || r.sellerSku || '';
                const cleanBase = base.replace(/-FBA$|-FBM$/i, '');
                return { ...r, fulfillment: o.v, sellerSku: `${cleanBase}-${o.v}` };
              })); 
              onClose(); 
            }} style={{
              flex:1, textAlign:'left', height:54, padding:'0 14px', border:`1px solid ${C.border}`,
              borderRadius:12, background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', gap:10, transition:'all .15s', fontFamily:'inherit'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = C.primary;
              e.currentTarget.style.background = C.bgLight;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = C.border;
              e.currentTarget.style.background = '#fff';
            }}>
              <div style={{ width:28, height:28, background:o.bg, color:o.color, borderRadius:8, display:'grid', placeItems:'center', flexShrink:0, fontSize:12, fontWeight:700 }}>{o.icon}</div>
              <div>
                <div style={{ fontSize:13, fontWeight:700 }}>{o.v}</div>
                <div style={{ fontSize:11, color:C.text3, marginTop: 1 }}>{o.t}</div>
              </div>
            </button>
          ))}
        </div>
        {closeBtn}
      </div>
    </div>
  );

  if (kind === 'price') return (
    <div style={wrap}>
      <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
        <div style={{ flexShrink:0 }}>
          <div style={{ fontSize:13, fontWeight:700, whiteSpace:'nowrap' }}>Set price</div>
          <div style={{ fontSize:12, color:C.text3, marginTop:2 }}>{count} SKUs</div>
        </div>
        {/* Mode tabs */}
        <div style={{ display:'flex', gap:4, padding:4, background:'#F2F3F5', borderRadius:10, flexShrink:0 }}>
          {[
            { k:'fixed', t:'Fixed' },
            { k:'buybox', t:'Buy Box ±' }
          ].map(o => (
            <button key={o.k} onClick={() => setMode(o.k)} style={{
              padding:'6px 14px', borderRadius:8, border:'none',
              background: mode === o.k ? '#fff' : 'transparent',
              color: mode === o.k ? C.primary : C.text2,
              fontSize:11, fontWeight:600, cursor:'pointer',
              boxShadow: mode === o.k ? '0 2px 4px rgba(0,0,0,0.06)' : 'none',
              transition: 'all .15s',
              fontFamily:'inherit'
            }}>{o.t}</button>
          ))}
        </div>
        {/* Mode controls */}
        <div style={{ flex:1, display:'flex', alignItems:'center', gap:14, minWidth:220 }}>
          {mode === 'fixed' && (
            <>
              <div style={{ display:'flex', alignItems:'center', border:`1px solid ${C.border}`, borderRadius:6, height:36, background:'#fff', maxWidth:180, flex:1 }}>
                <span style={{ padding:'0 12px', color:C.text3, fontSize:12, fontWeight:500, borderRight:`1px solid ${C.borderSoft}` }}>$</span>
                <input value={val} onChange={e => setVal(e.target.value)} placeholder="0.00" autoFocus
                  style={{ flex:1, border:'none', outline:'none', padding:'0 12px', fontSize:12, height:'100%', background:'transparent', fontFamily:'inherit' }}/>
              </div>
              <span style={{ fontSize:11, color:C.text3, whiteSpace:'nowrap' }}>applied to all selected</span>
            </>
          )}
          {mode === 'buybox' && (
            <>
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ color:C.text3, fontSize:11 }}>Buy Box</span>
                <div style={{ display:'flex', alignItems:'center', border:`1px solid ${C.border}`, borderRadius:6, height:36, background:'#fff', overflow:'hidden' }}>
                  <select value={bbSign} onChange={e => setBbSign(e.target.value)} style={{ border:'none', outline:'none', fontSize:14, fontWeight:700, background:'#F8FAFC', padding:'0 10px', height:'100%', cursor:'pointer', color:C.text2, borderRight:`1px solid ${C.border}` }}>
                    <option value="+">+</option>
                    <option value="-">-</option>
                  </select>
                  <span style={{ paddingLeft:10, color:C.text3, fontSize:12 }}>$</span>
                  <input value={bbOffset} onChange={e => setBbOffset(e.target.value)} placeholder="0.00"
                    style={{ width:80, border:'none', outline:'none', padding:'0 12px', fontSize:12, height:'100%', background:'transparent', fontFamily:'inherit' }}/>
                </div>
              </div>
              <span style={{ fontSize:11, color:C.text3 }}>for SKUs with active Buy Box</span>
            </>
          )}
        </div>
        <button
          onClick={() => {
            onApply(rows => rows.map(r => {
              if (!selectedRows.some(s => String(s.id) === String(r.id))) return r;
              let price = '';
              if (mode === 'fixed') {
                price = val;
              } else if (mode === 'buybox') {
                const basePrice = r.buyBox != null ? r.buyBox : (Number(r.price) || 0);
                const off = parseFloat(bbOffset) || 0;
                const nv = bbSign === '+' ? (basePrice + off) : (basePrice - off);
                price = Math.max(0, nv).toFixed(2);
              } else {
                price = r.price;
              }
              const pNum = Number(price);
              return { ...r, price, priceErr: !price || isNaN(pNum) || pNum <= 0 || pNum > 999 };
            }));
            onClose();
          }}
          disabled={(mode === 'fixed' && !val) || (mode === 'buybox' && !bbOffset)}
          className="blm-btn-primary"
          style={{
            padding:'7px 16px', borderRadius:6, border:'none',
            background: ((mode === 'fixed' && !val) || (mode === 'buybox' && !bbOffset)) ? '#B5C7E5' : C.primary,
            color:'#fff', fontSize:12, fontWeight:600,
            cursor: ((mode === 'fixed' && !val) || (mode === 'buybox' && !bbOffset)) ? 'not-allowed' : 'pointer',
            flexShrink:0, fontFamily:'inherit',
            transition: 'all 0.15s'
          }}>Apply</button>
        {closeBtn}
      </div>
    </div>
  );

  return null;
};

/* ─── CSS (injected once) ────────────────────────────────────────────────── */
const CSS = `
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes blm-slideUp { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
  .blm-root input::-webkit-outer-spin-button, .blm-root input::-webkit-inner-spin-button { -webkit-appearance:none; margin:0; }
  .blm-root input[type=number] { -moz-appearance:textfield; }
  .blm-wrap .modal-dialog { max-width:1150px !important; width:95vw !important; margin:1.75rem auto !important; }
  .blm-wrap .modal-content { max-height:90vh !important; display:flex !important; flex-direction:column !important; overflow:hidden !important; }
  .blm-wrap .modal-header { display:none !important; }
  .blm-wrap .modal-footer { display:none !important; }
  .blm-wrap .modal-body { padding:0 !important; flex:1 !important; display:flex !important; flex-direction:column !important; overflow:hidden !important; min-height:0 !important; }
  .blm-wrap .ant-spin-nested-loading, .blm-wrap .ant-spin-container { display:flex !important; flex-direction:column !important; flex:1 !important; min-height:0 !important; height:100% !important; }
  .blm-root { display:flex; flex-direction:column; height:100%; flex:1; min-height:0; font-family:'Inter',sans-serif !important; color:#1F1F1F; box-sizing:border-box; }
  .blm-root *, .blm-root button, .blm-root input, .blm-root select { font-family:'Inter',sans-serif !important; box-sizing:border-box; }
  .blm-table-area { flex:1; overflow-y:auto; min-height:0; }
  .blm-grid { display:grid; grid-template-columns:40px minmax(260px,2.4fr) 140px 160px 150px 32px; gap:16px; border-bottom:1px solid #F0F2F5; }
  .blm-grid-head { font-size:11.5px !important; font-weight:600; text-transform:uppercase; letter-spacing:0.04em; color:#6B7280; padding:10px 22px; background:#fff; position:sticky; top:0; z-index:2; border-bottom:1px solid #E8ECF1; }
  .blm-grid-row { padding:10px 22px; align-items:center; font-size:13px !important; transition:background .1s; cursor:default; position:relative; }
  .blm-grid-row.selected { background:#EEF4FD; }
  .blm-grid-row .blm-row-accent { position:absolute; left:0; top:0; bottom:0; width:3px; background:#1565D8; display:none; }
  .blm-grid-row.selected .blm-row-accent { display:block; }
  .blm-remove-btn { opacity:0; width:24px; height:24px; border-radius:5px; border:none; background:transparent; cursor:pointer; color:#7E8A90; display:grid; placeItems:center; transition:opacity .12s; }
  .blm-grid-row:hover .blm-remove-btn { opacity:1; }
  .blm-remove-btn:hover { background:#FDECEF; color:#F52C50; }
  .blm-product-name { font-size:14px !important; font-weight:500; color:#1B2559; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-bottom:2px; display:block; }
  .blm-asin-badge { padding:2px 6px; background-color:#F0F4FF; color:#1565D8; border:1px solid #D6E4FF; font-size:10px !important; border-radius:6px; line-height:normal; display:flex; align-items:center; gap:4px; max-width:max-content; }
  .blm-sku-badge { padding:2px 6px; background-color:#F5F5F5; color:#4E5969; border:1px solid #E8ECF1; font-size:10px !important; border-radius:6px; line-height:normal; display:flex; align-items:center; gap:4px; max-width:max-content; }
  .blm-badge-win { font-size:9px; padding:1px 5px; border-radius:3px; background:#E8F7EF; color:#009c34; font-weight:700; }
  .blm-badge-high { font-size:9px; padding:1px 5px; border-radius:3px; background:#FEF3E2; color:#B45309; font-weight:700; }
  .blm-search-wrap { display:flex; align-items:center; gap:8px; border:1px solid #E8ECF1; border-radius:8px; padding:0 12px; background:#fff; flex:1; }
  .blm-search-wrap:focus-within { border-color:#1565D8; box-shadow:0 0 0 3px #DCE9FB; }
  .blm-search-input { border:none; outline:none; font-size:12px; padding:8px 0; flex:1; background:transparent; color:#1F1F1F; font-family:'Inter',sans-serif !important; }
  .blm-pill { display:inline-flex; align-items:center; gap:5px; padding:5px 10px; border-radius:20px; font-size:11px; font-weight:600; }
  .blm-bulk-pill { padding:5px 10px; border-radius:6px; font-size:11px; font-weight:600; cursor:pointer; display:inline-flex; align-items:center; gap:6px; transition:all .12s; font-family:'Inter',sans-serif !important; }
  .blm-page-btn { border:1px solid #E8ECF1; background:#fff; color:#535E63; border-radius:6px; width:30px; height:30px; display:inline-flex; align-items:center; justify-content:center; cursor:pointer; font-size:12px; font-family:'Inter',sans-serif !important; transition:all .15s; }
  .blm-page-btn:hover:not(:disabled) { border-color:#1565D8; color:#1565D8; }
  .blm-page-btn:disabled { opacity:0.35; cursor:not-allowed; }
  .blm-page-btn.active { background:#1565D8; border-color:#1565D8; color:#fff !important; font-weight:600; }
  .blm-bulk-pill:hover { background: rgba(255,255,255,0.12) !important; color: #fff !important; }
  .blm-bulk-pill.active { background: #1565D8 !important; color: #fff !important; }
  .blm-bulk-pill.active:hover { background: #1565D8 !important; color: #fff !important; opacity: 0.9; }
  .blm-btn-primary:hover:not(:disabled) { background: #1565D8 !important; color: #fff !important; opacity: 0.9; }
`;

/* ─── Main modal ─────────────────────────────────────────────────────────── */
const BulkListingModal = ({ open, onHide, selectedItems }) => {
  const dispatch = useDispatch();
  const { submittingListingsFeed } = useSelector(s => s.threeLayerProducts || {});

  const [rows, setRows]           = useState([]);
  const [selected, setSelected]   = useState(new Set());
  const [search, setSearch]       = useState('');
  const [activePanel, setActivePanel] = useState(null);
  const [pageSize, setPageSize]   = useState(10);
  const [page, setPage]           = useState(1);
  const [initialized, setInitialized] = useState(false);
  const { listingStatuses = {}, buyBoxPrices = {}, fetchingBuyBoxPrices } = useSelector(state => state.threeLayerProducts || {});
  

  /* inject CSS once */
  useEffect(() => {
    if (document.getElementById('blm-styles')) return;
    const s = document.createElement('style');
    s.id = 'blm-styles';
    s.textContent = CSS;
    document.head.appendChild(s);
  }, []);

  useEffect(() => {
    if (open && selectedItems?.length) {
      setInitialized(false);
      setRows([]);
      const skuIds = selectedItems.map(item => item.id);
      dispatch(GetBuyBoxPrices({ skuIds }));
      setSearch('');
      setPage(1);
      setActivePanel(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Initialize rows once prices are fetched */
  useEffect(() => {
    if (open && !fetchingBuyBoxPrices && !initialized && selectedItems?.length) {
      const init = cloneDeep(selectedItems)
        .filter(item => {
          const s = listingStatuses[item.id]?.status;
          const ps = listingStatuses[item.id]?.processing_status;
          return s !== 'DONE' && s !== 'IN_PROGRESS' && ps !== 'DONE' && ps !== 'IN_PROGRESS' && ps !== 'IN_QUEUE';
        })
        .map(item => {
          const bbPrice = buyBoxPrices[item.id] !== undefined ? buyBoxPrices[item.id] : item.buy_box_price;
          const initialPrice = bbPrice ? String(bbPrice) : '';
          return {
            id:          item.id,
            sellerSku:   item.sku || '',
            baseSku:     item.sku ? item.sku.replace(/-FBA$|-FBM$/i, '') : '',
            asin:        item.vendorasin?.asin || '',
            title:       item.item_name || '',
            imageUrl:    item.image_url || '',
            price:       initialPrice,
            quantity:    item.item_quantity ?? item.total_quantity ?? 0,
            conditionType: item.item_condition || 'New',
            fulfillment: item.fulfillment_channel || 'FBA',
            cogs:        item.cogs || null,
            buyBox:      (bbPrice !== null && bbPrice !== undefined) ? parseFloat(bbPrice) : null,
            priceErr:    !initialPrice || isNaN(Number(initialPrice)) || Number(initialPrice) <= 0 || Number(initialPrice) > 999
          };
        });
      
      setRows(sortBy(init, r => r.priceErr ? 0 : 1));
      setSelected(new Set(init.filter(r => {
        const s = listingStatuses[r.id]?.status;
        return s !== 'DONE' && s !== 'IN_PROGRESS';
      }).map(r => r.id)));
      setInitialized(true);
    }
  }, [open, fetchingBuyBoxPrices, initialized, selectedItems, buyBoxPrices, listingStatuses]);

  useEffect(() => {
    setSelected(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const id of prev) {
        const s = listingStatuses[id]?.status;
        const ps = listingStatuses[id]?.processing_status;
        if (s === 'DONE' || s === 'IN_PROGRESS' || ps === 'DONE' || ps === 'IN_PROGRESS' || ps === 'IN_QUEUE') {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [listingStatuses]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      r.sellerSku?.toLowerCase().includes(q) ||
      r.asin?.toLowerCase().includes(q) ||
      r.title?.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const paginated  = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  useEffect(() => { setPage(1); }, [search]);

  const validCount = rows.filter(r => !r.priceErr).length;
  const errCount   = rows.length - validCount;

  const selectable = useMemo(() => filtered.filter(r => {
    const s = listingStatuses[r.id]?.status;
    const ps = listingStatuses[r.id]?.processing_status;
    return s !== 'DONE' && s !== 'IN_PROGRESS' && ps !== 'DONE' && ps !== 'IN_PROGRESS' && ps !== 'IN_QUEUE';
  }), [filtered, listingStatuses]);

  const allSel  = selectable.length > 0 && selectable.every(r => selected.has(r.id));
  const someSel = selectable.some(r => selected.has(r.id)) && !allSel;

  const toggleAll = useCallback(() => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allSel) selectable.forEach(r => next.delete(r.id));
      else        selectable.forEach(r => next.add(r.id));
      return next;
    });
  }, [allSel, selectable]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        toggleAll();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, toggleAll]);

  const toggle = id => {
    const s = listingStatuses[id]?.status;
    const ps = listingStatuses[id]?.processing_status;
    if (s === 'DONE' || s === 'IN_PROGRESS' || ps === 'DONE' || ps === 'IN_PROGRESS' || ps === 'IN_QUEUE') return;
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const updateRow = useCallback((id, patch) => {
    setRows(prev => prev.map(r => {
      if (r.id !== id) return r;
      const n = { ...r, ...patch };
      if ('price' in patch) {
        const p = Number(n.price);
        n.priceErr = n.price === '' || isNaN(p) || p <= 0 || p > 999;
      }
      if ('sellerSku' in patch) n.dupErr = false;
      return n;
    }));
  }, []);

  const removeRow = id => {
    setRows(prev => prev.filter(r => r.id !== id));
    setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
  };

  const selectedRows = rows.filter(r => selected.has(r.id));

  const handleSubmit = async () => {
    const rowsForSubmit = rows.filter(r => {
      const s = listingStatuses[r.id]?.status;
      const ps = listingStatuses[r.id]?.processing_status;
      return s !== 'DONE' && s !== 'IN_PROGRESS' && ps !== 'DONE' && ps !== 'IN_PROGRESS' && ps !== 'IN_QUEUE';
    });
    
    if (rowsForSubmit.length === 0) {
      notification.error({ top:65, message:'No Items', description:'There are no eligible items to list.' });
      return;
    }

    if (rowsForSubmit.some(r => r.priceErr)) {
      notification.error({ top:65, message:'Invalid Prices', description:'Please fix invalid prices (must be between 0.01 and 999) before submitting.' });
      return;
    }


    const skuData = rowsForSubmit.map(r => ({
      skuId:              r.id,
      sellerSku:          r.sellerSku,
      asin:               r.asin,
      price:              Number(r.price),
      quantity:           r.quantity || 1,
      conditionType:      r.conditionType || 'New',
      fulfillmentChannel: r.fulfillment || 'FBA'
    }));
    const result = await dispatch(SubmitListingsFeed({ skuData }));
    if (result?.payload?.success) {
      notification.success({ top:65, message:'Listings Queued', description:'Bulk listing job queued. Amazon will process it shortly.' });
      onHide();
      setTimeout(() => window.location.reload(), 1500);
    } else {
      const dupErrors = result?.payload?.errorData?.duplicateErrors;
      if (Array.isArray(dupErrors) && dupErrors.length > 0) {
        notification.error({ top:65, message:'Duplicate SKUs', description: result?.payload?.message || 'Some SKUs already exist in your catalog.' });
        setRows(prev => {
          const next = prev.map(r => ({ ...r, dupErr: dupErrors.includes(r.id) }));
          return sortBy(next, r => r.dupErr ? 0 : (r.priceErr ? 1 : 2));
        });
      } else {
        notification.error({ top:65, message:'Submission Failed', description: result?.payload?.message || 'Something went wrong.' });
      }
    }
  };

  return (
    <Modal open={open} onHide={onHide} header={true} heading="" closeButton={false} size="xl" className="blm-wrap">
      <Spin spinning={submittingListingsFeed || fetchingBuyBoxPrices} tip={fetchingBuyBoxPrices ? 'Fetching Buy Box Prices...' : 'Submitting...'} style={{ display:'flex', flexDirection:'column', flex:1, height:'100%' }}>
        <div className="blm-root">

          {/* ══════════ HEADER ══════════ */}
          <div style={{ padding:'16px 22px 12px', borderBottom:`1px solid ${C.borderSoft}`, flexShrink:0 }}>
            <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
              {/* Amazon icon box */}
              <div style={{ width:40, height:40, borderRadius:10, background:'#FFF7E0', display:'grid', placeItems:'center', flexShrink:0 }}>
                <img src={AmazonLogo} alt="Amazon" style={{ width:24, height:24, objectFit:'contain' }}/>
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', alignItems:'baseline', gap:10, flexWrap:'wrap' }}>
                  <h2 style={{ margin:0, fontSize:17, fontWeight:700, letterSpacing:-0.2 }}>List on Amazon</h2>
                  <span style={{ fontSize:12, color:C.text3 }}>{rows.length} SKUs · {selected.size} selected</span>
                </div>
                <div style={{ fontSize:12, color:C.text2, marginTop:3, lineHeight:1.5 }}>
                  Review and adjust price and fulfillment before submitting. Use bulk actions to update many at once.
                </div>
              </div>
              <button onClick={onHide} style={{ width:32, height:32, borderRadius:8, border:`1px solid ${C.borderSoft}`, background:'#fff', display:'grid', placeItems:'center', cursor:'pointer', color:C.text2, flexShrink:0 }}>
                <I.X size={16}/>
              </button>
            </div>

            {/* Validation strip */}
            <div style={{ display:'flex', gap:8, marginTop:12, alignItems:'center', flexWrap:'wrap' }}>
              {/* Search */}
              <div className="blm-search-wrap" style={{ maxWidth:260 }}>
                <I.Search size={13} color={C.text3}/>
                <input className="blm-search-input" placeholder="Search SKU / ASIN / Title" value={search} onChange={e => setSearch(e.target.value)}/>
                {search && <button onClick={() => setSearch('')} style={{ background:'none', border:'none', cursor:'pointer', color:C.text3, padding:0 }}><I.X size={12}/></button>}
              </div>
              <div style={{ flex:1 }}/>
              <span className="blm-pill" style={{ background:C.successBg, color:C.success }}>
                <I.Check size={12}/> {validCount} ready
              </span>
              {errCount > 0 && (
                <span className="blm-pill" style={{ background:C.warningBg, color:C.warning }}>
                  <I.Warn size={12}/> {errCount} need attention
                </span>
              )}
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              </div>
            </div>
          </div>

          {/* ══════════ BULK ACTION BAR ══════════ */}
          <div style={{
            padding:'9px 22px',
            background: selected.size > 0 ? C.dark : C.bgLight,
            borderBottom:`1px solid ${C.borderSoft}`,
            display:'flex', alignItems:'center', gap:10, flexShrink:0,
            transition:'background .2s ease', position:'relative'
          }}>
            {selected.size > 0 ? (
              <>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:12, fontWeight:600, color:'#fff' }}>{selected.size} selected</span>
                  <span style={{ fontSize:11, color:'rgba(255,255,255,0.5)' }}>· apply bulk action</span>
                </div>
                <div style={{ width:1, height:18, background:'rgba(255,255,255,0.15)' }}/>
                <button
                  className={`blm-bulk-pill${activePanel === 'fulfill' ? ' active' : ''}`}
                  onClick={() => setActivePanel(p => p === 'fulfill' ? null : 'fulfill')}
                  style={{
                    border:`1px solid ${activePanel === 'fulfill' ? C.primary : 'rgba(255,255,255,0.2)'}`,
                    background: activePanel === 'fulfill' ? C.primary : 'rgba(255,255,255,0.07)',
                    color:'#fff'
                  }}>
                  <I.Truck size={13}/> Fulfillment <I.ChevD size={11}/>
                </button>
                <button
                  className={`blm-bulk-pill${activePanel === 'price' ? ' active' : ''}`}
                  onClick={() => setActivePanel(p => p === 'price' ? null : 'price')}
                  style={{
                    border:`1px solid ${activePanel === 'price' ? C.primary : 'rgba(255,255,255,0.2)'}`,
                    background: activePanel === 'price' ? C.primary : 'rgba(255,255,255,0.07)',
                    color:'#fff'
                  }}>
                  <I.Wallet size={13}/> Price <I.ChevD size={11}/>
                </button>
                <div style={{ flex:1 }}/>
                <button
                  onClick={() => { selectedRows.forEach(r => removeRow(r.id)); setActivePanel(null); }}
                  style={{ padding:'5px 10px', borderRadius:6, border:'1px solid rgba(255,255,255,0.15)', background:'transparent', color:'#fff', fontSize:11, cursor:'pointer', display:'flex', alignItems:'center', gap:5, fontFamily:'inherit' }}>
                  <I.Trash size={11}/> Remove
                </button>
                <button
                  onClick={() => setSelected(new Set())}
                  style={{ padding:'5px 10px', borderRadius:6, border:'none', background:'transparent', color:'rgba(255,255,255,0.55)', fontSize:11, cursor:'pointer', fontFamily:'inherit' }}>
                  Clear
                </button>
              </>
            ) : (
              <>
                <span onClick={toggleAll} style={{ fontSize:12, fontWeight:600, color:C.text2, cursor:'pointer' }}>Select all {filtered.length} items</span>
                <div style={{ flex:1 }}/>
                <span style={{ fontSize:11, color:C.text2, fontWeight:500 }}>Tip: <kbd style={{ background:'#E8ECF1', border:`1px solid ${C.border}`, borderRadius:4, padding:'2px 5px', fontSize:10, color:C.text, fontWeight:700, fontFamily:'inherit' }}>⌘A</kbd> select all</span>
              </>
            )}

            {/* Floating panel */}
            {activePanel && (
              <BulkPanel
                kind={activePanel}
                count={selected.size}
                selectedRows={selectedRows}
                onApply={updater => setRows(updater)}
                onClose={() => setActivePanel(null)}
              />
            )}
          </div>

          {/* ══════════ CONTENT AREA (Blurred/Disabled when Panel open) ══════════ */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            opacity: activePanel ? 0.4 : 1,
            pointerEvents: activePanel ? 'none' : 'auto',
            transition: 'opacity 0.2s ease',
            position: 'relative'
          }}>
            {/* ══════════ TABLE ══════════ */}
            <div className="blm-table-area">
            {/* Header row */}
            <div className="blm-grid blm-grid-head" style={{ alignItems:'center' }}>
              <div style={{ display:'flex', alignItems:'center' }}>
                <Chk checked={allSel} indeterminate={someSel} onChange={toggleAll} size={16}/>
              </div>
              <div>Product</div>
              <div style={{ textAlign:'center' }}>Fulfillment</div>
              <div>Price <span style={{ color:C.danger }}>*</span></div>
              <div>Buy Box</div>
              <div/>
            </div>

            {/* Data rows */}
            {paginated.length === 0 ? (
              <div style={{ padding:48, textAlign:'center', color:C.text3, fontSize:13 }}>
                {search ? 'No SKUs match your search.' : 'No items to display.'}
              </div>
            ) : paginated.map(row => {
              const isSelected = selected.has(row.id);
              const price = parseFloat(row.price);
              const bb = row.buyBox != null ? parseFloat(row.buyBox) : null;
              const showWin  = !!bb && !isNaN(price) && price < bb - 0.5;
              const showHigh = !!bb && !isNaN(price) && price > bb + 0.5;
              return (
                <div
                  key={row.id}
                  className={`blm-grid blm-grid-row${isSelected ? ' selected' : ''}`}
                >
                  <div className="blm-row-accent"/>
                  <div style={{ display:'flex', alignItems:'center' }}>
                    <Chk 
                      checked={isSelected} 
                      onChange={() => toggle(row.id)} 
                      size={16}
                      disabled={
                        listingStatuses[row.id]?.status === 'DONE' || 
                        listingStatuses[row.id]?.status === 'IN_PROGRESS' ||
                        listingStatuses[row.id]?.processing_status === 'DONE' ||
                        listingStatuses[row.id]?.processing_status === 'IN_PROGRESS' ||
                        listingStatuses[row.id]?.processing_status === 'IN_QUEUE'
                      }
                    />
                  </div>
                  {/* Product column */}
                  <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                    <img src={row.imageUrl || ProductImg} alt="product" style={{ width:32, height:32, borderRadius:5, objectFit:'contain', border:`1px solid ${C.borderSoft}`, flexShrink:0 }}/>
                    <div style={{ minWidth:0, flex:1 }}>
                      <span className="blm-product-name" title={row.title}>{row.title || '—'}</span>
                      <div style={{ display:'flex', gap:6, alignItems:'center', marginTop:4 }}>
                        {row.asin && (
                          <div className="blm-asin-badge">
                            <span style={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.asin}</span>
                            <Tooltip title="Copy ASIN">
                              <img src={copyIcona} alt="copy" style={{ width:11, height:11, cursor:'pointer', opacity:0.8 }}
                                onClick={() => copyToClip(row.asin)}/>
                            </Tooltip>
                          </div>
                        )}
                        {row.editingSku ? (
                          <input 
                            value={row.sellerSku || ''} 
                            autoFocus
                            onBlur={() => updateRow(row.id, { editingSku: false })}
                            onKeyDown={e => { if (e.key === 'Enter') updateRow(row.id, { editingSku: false }); }}
                            onChange={e => updateRow(row.id, { sellerSku: e.target.value })}
                            style={{ border: row.dupErr ? `1px solid ${C.danger}` : `1px solid ${C.borderSoft}`, borderRadius: 4, padding: '2px 8px', fontSize: 10, background: '#fff', width: 140, outline: 'none', boxShadow: row.dupErr ? `0 0 0 2px ${C.danger}15` : 'none' }}
                          />
                        ) : (
                          <div className="blm-sku-badge" style={row.dupErr ? { borderColor:C.danger, color:C.danger, background:'#FDECEF' } : {}}>
                            <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.sellerSku}</span>
                            <Tooltip title="Copy SKU">
                              <img src={copyIcona} alt="copy" style={{ width:11, height:11, cursor:'pointer', opacity:0.8 }}
                                onClick={() => copyToClip(row.sellerSku)}/>
                            </Tooltip>
                            <I.Pencil size={11} style={{ cursor: 'pointer', opacity: 0.8, color: C.primary, marginLeft: 2 }} onClick={() => updateRow(row.id, { editingSku: true })} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Fulfillment */}
                  <div style={{ display:'flex', justifyContent:'center' }}>
                    <SegPill value={row.fulfillment} onChange={v => updateRow(row.id, { fulfillment: v })}/>
                  </div>
                  {/* Price */}
                  <div>
                    <PriceInput
                      value={row.price}
                      onChange={v => updateRow(row.id, { price: v })}
                      error={row.priceErr}
                      cogs={row.cogs}
                    />
                  </div>
                  {/* Buy Box */}
                  <div>
                    <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:12 }}>
                      <span style={{ color:C.text2, fontWeight:600 }}>${bb != null ? (bb || 0).toFixed(2) : '0.00'}</span>
                      {showWin  && <span className="blm-badge-win">WIN</span>}
                      {showHigh && <span className="blm-badge-high">HIGH</span>}
                    </div>
                  </div>
                  {/* Remove */}
                  <div style={{ display:'grid', placeItems:'center' }}>
                    <button className="blm-remove-btn" onClick={() => removeRow(row.id)} title="Remove" style={{ display:'grid', placeItems:'center' }}>
                      <I.X size={13}/>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ══════════ PAGINATION ══════════ */}
          {filtered.length > 0 && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 22px', borderTop:`1px solid ${C.borderSoft}`, flexShrink:0, flexWrap:'wrap', gap:8, background:C.bgLight }}>
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ fontSize:13, color:C.text3 }}>Rows per page:</span>
                {[5,10,20,50].map(n => (
                  <button key={n} className={`blm-page-btn${pageSize === n ? ' active' : ''}`}
                    onClick={() => { setPageSize(n); setPage(1); }}
                    style={{ width:'auto', padding:'0 8px' }}
                  >{n}</button>
                ))}
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                <span style={{ fontSize:13, color:C.text3, marginRight:4 }}>
                  {(safePage-1)*pageSize+1}–{Math.min(safePage*pageSize, filtered.length)} of {filtered.length}
                </span>
                <button className="blm-page-btn" disabled={safePage===1} onClick={() => setPage(1)}>«</button>
                <button className="blm-page-btn" disabled={safePage===1} onClick={() => setPage(p => p-1)}>‹</button>
                {Array.from({length:totalPages},(_,i)=>i+1)
                  .filter(p => p===1||p===totalPages||Math.abs(p-safePage)<=1)
                  .reduce((acc,p,idx,arr)=>{ if(idx>0&&p-arr[idx-1]>1) acc.push('…'); acc.push(p); return acc; },[])
                  .map((p,i) => p==='…'
                    ? <span key={`e${i}`} style={{fontSize:13,color:C.text3,padding:'0 2px'}}>…</span>
                    : <button key={p} className={`blm-page-btn${safePage===p?' active':''}`} onClick={()=>setPage(p)}>{p}</button>
                  )}
                <button className="blm-page-btn" disabled={safePage===totalPages} onClick={() => setPage(p => p+1)}>›</button>
                <button className="blm-page-btn" disabled={safePage===totalPages} onClick={() => setPage(totalPages)}>»</button>
              </div>
            </div>
          )}

          {/* ══════════ FOOTER ══════════ */}
          <div style={{ padding:'12px 22px', borderTop:`1px solid ${C.borderSoft}`, display:'flex', alignItems:'center', gap:12, background:C.bgLight, flexShrink:0 }}>
            <span style={{ fontSize:11, color:C.text3 }}>
              Submitting will create live listings. Changes take 5–15 minutes to appear in Seller Central.
            </span>
            <div style={{ flex:1 }}/>
            <button onClick={onHide} style={{ padding:'8px 16px', borderRadius:8, border:`1px solid ${C.border}`, background:'#fff', fontSize:13, fontWeight:600, color:C.text2, cursor:'pointer', fontFamily:'inherit' }}>
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submittingListingsFeed || rows.length === 0 || rows.some(r => r.priceErr)}
              style={{
                padding:'8px 18px', borderRadius:8, border:'none',
                background: (rows.length === 0 || rows.some(r => r.priceErr)) ? '#B5C7E5' : C.primary,
                color:'#fff', fontSize:13, fontWeight:600,
                cursor: (rows.length === 0 || rows.some(r => r.priceErr)) ? 'not-allowed' : 'pointer',
                display:'flex', alignItems:'center', gap:8, fontFamily:'inherit',
                boxShadow: (rows.length === 0 || rows.some(r => r.priceErr)) ? 'none' : '0 1px 3px rgba(21,101,216,0.3)'
              }}>
              Submit {rows.length} {rows.length === 1 ? 'listing' : 'listings'}
              {rows.some(r => r.priceErr) && <span style={{ fontSize:11, opacity:0.8 }}>(fix errors)</span>}
            </button>
          </div>
        </div>
      </div>
    </Spin>
    </Modal>
  );
};

export default BulkListingModal;
